import {
  BadgeCheck,
  Briefcase,
  Building2,
  ClipboardCheck,
  ClipboardList,
  FilePenLine,
  FilePlus,
  FileText,
  GraduationCap,
  HeartHandshake,
  ListOrdered,
  Mail,
  Megaphone,
  Newspaper,
  PenLine,
  Reply,
  Scale,
  ShieldCheck,
  UserRound,
  type LucideIcon
} from "lucide-react";
import { useCallback, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { IconTile } from "@/components/ui/icon-tile";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

import { WorkspaceTreeAPI } from "./api/workspaceTreeApi";
import type { DraftTypeOption } from "./types";

const CUSTOM = "custom";

const CUSTOM_OPTION: DraftTypeOption = {
  draft_type: CUSTOM,
  label: "Custom",
  description: "Anything else the attorney asks for.",
  suggested: false,
  empty_slot_id: null,
  intake_prompt: null
};

const ICONS: Record<string, LucideIcon> = {
  legal_brief: Scale,
  support_letter: FilePenLine,
  cover_letter: Mail,
  exhibit_list: ListOrdered,
  personal_statement: UserRound,
  expert_letter: GraduationCap,
  rfe_response: Reply,
  noir_noid_response: Reply,
  hardship_statement_petitioner: HeartHandshake,
  hardship_statement_beneficiary: HeartHandshake,
  asylum_affidavit: ShieldCheck,
  certified_statement: BadgeCheck,
  job_description: Briefcase,
  narrative_recruitment_report: ClipboardList,
  employment_verification_letter: Building2,
  newspaper_ad: Newspaper,
  swa_job_order: ClipboardCheck,
  notice_of_filing: Megaphone,
  [CUSTOM]: PenLine
};

const ITEM = "h-auto items-start gap-2.5 py-2";

/** Suggested types first, "Custom" last, whatever order the server sent. */
export const orderDraftTypes = (
  options: DraftTypeOption[]
): DraftTypeOption[] => {
  const typed = options.filter((o) => o.draft_type !== CUSTOM);
  return [...typed, CUSTOM_OPTION];
};

type Props = {
  individualId: string;
  onPick: (option: DraftTypeOption) => void;
};

/**
 * The work-products pane's New Draft button: a menu of the draft types the
 * case's visa and workflow call for. A type the case already has an empty
 * draft for says so, and picking it opens that draft. Loaded on open so the
 * empty slots are current.
 */
export const NewDraftMenu: FC<Props> = ({ individualId, onPick }) => {
  const [options, setOptions] = useState<DraftTypeOption[] | null>(null);
  const load = useCallback(
    (open: boolean) => {
      if (!open) return;
      WorkspaceTreeAPI.getDraftTypes(individualId)
        .then((r) => setOptions(orderDraftTypes(r.draft_types)))
        .catch(() => setOptions(orderDraftTypes([])));
    },
    [individualId]
  );
  const typed = (options ?? []).filter((o) => o.draft_type !== CUSTOM);

  return (
    <DropdownMenu onOpenChange={load}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label="New draft"
              data-testid="new-draft-menu"
            >
              <FilePlus className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">New Draft</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-80">
        {options === null ? (
          <div className="flex items-center gap-2 px-2 py-2 text-sm text-fg-3">
            <Spinner className="size-3.5" />
            Loading Draft Types…
          </div>
        ) : (
          <>
            {typed.length ? (
              <DropdownMenuLabel>Suggested For This Case</DropdownMenuLabel>
            ) : null}
            {typed.map((option) => (
              <DropdownMenuItem
                key={option.draft_type}
                onSelect={() => onPick(option)}
                data-testid={`new-draft-${option.draft_type}`}
                className={ITEM}
              >
                <IconTile icon={ICONS[option.draft_type] ?? FileText} />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex min-h-6 items-center justify-between gap-2">
                    <span className="truncate text-base font-medium text-fg">
                      {option.label}
                    </span>
                    {option.empty_slot_id ? (
                      <StatusBadge>Not Started</StatusBadge>
                    ) : null}
                  </span>
                  {option.description ? (
                    <span className="line-clamp-2 text-sm text-fg-3">
                      {option.description}
                    </span>
                  ) : null}
                </span>
              </DropdownMenuItem>
            ))}
            {typed.length ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem
              onSelect={() => onPick(CUSTOM_OPTION)}
              data-testid="new-draft-custom"
              className={ITEM}
            >
              <IconTile icon={PenLine} />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex min-h-6 items-center text-base font-medium text-fg">
                  Custom
                </span>
                <span className="text-sm text-fg-3">
                  {CUSTOM_OPTION.description}
                </span>
              </span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
